import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  input,
} from '@angular/core';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { GenericSceneController } from '../../services/controllers/scene-controllers/scene-controller-factory';
import { ReplayControlType } from 'src/games/common/controllers/replay-runner';
import { Subject, takeUntil } from 'rxjs';
import { PlaybackState } from 'src/games/common/controllers/replay-timeline';
import { Room } from 'src/app/core/services/rooms-results/models/room';
import { RoomTimer } from 'src/app/core/services/rooms-results/models/room-timer';
import {
  DuelResultDrawerData,
  DuelResultDrawerViewModel,
} from 'src/app/features/results/duel-result-drawer/duel-result-drawer-view-model';
import { GameId } from 'src/app/shared/models/game/game-id';
import { DuelResultDrawerComponent } from 'src/app/features/results/duel-result-drawer/duel-result-drawer.component';
import { MarblesResultDrawerComponent } from 'src/app/features/results/marbles-result-drawer/marbles-result-drawer.component';
import { TranslocoService } from '@jsverse/transloco';
import { ModalController, Platform } from '@ionic/angular/standalone';
import {
  getModalConfig,
  shouldShowAsSheetModal,
  modalContent,
} from 'src/app/core/services/modal/modal.utils';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { GameControlDisplay } from '../../services/game-ui/game-control-display';
import { ReplayGameControlsViewComponent } from '../../components/replay-game-controls-view/replay-game-controls-view.component';

@Component({
  selector: 'app-replay-game-controls',
  templateUrl: './replay-game-controls.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReplayGameControlsViewComponent],
})
export class ReplayGameControlsComponent implements OnInit, OnDestroy, GameControlDisplay {
  readonly gameController = input.required<GenericSceneController>();
  // game-host resets the game to loading through this when the result drawer is dismissed. It is
  // a callback, not an output, because the drawer can outlive these controls and the reset must
  // still run then, where Angular drops an output emitted after destroy.
  readonly resultDismissedCallback = input.required<() => void>();

  private themeService = inject(ThemeService);
  private translocoService = inject(TranslocoService);
  private modalController = inject(ModalController);
  private platform = inject(Platform);
  private analyticsService = inject(AnalyticsService);

  private hasControls = signal(true);
  readonly showingControls: Signal<boolean> = this.hasControls.asReadonly();

  totalSegments = signal(1);
  progressEvent = signal({ index: 0, progress: 0 });
  playbackSpeed = signal('1x');
  playIcon = signal('play');

  private destroy$ = new Subject<void>();

  async ngOnInit() {
    const runner = this.gameController().replayRunner;
    runner.replayMarkerCount$.pipe(takeUntil(this.destroy$)).subscribe((markerCount) => {
      this.totalSegments.set(markerCount);
    });
    runner.timelineProgress$.pipe(takeUntil(this.destroy$)).subscribe(({ marker, progress }) => {
      this.progressEvent.set({ index: marker, progress: progress * 100 });
    });
    runner.timelinePlaybackState$.pipe(takeUntil(this.destroy$)).subscribe((playbackState) => {
      this.playIcon.set(playbackState === PlaybackState.Playing ? 'pause' : 'play');
    });
    runner.timelinePlaybackSpeed$.pipe(takeUntil(this.destroy$)).subscribe((playbackSpeed) => {
      this.playbackSpeed.set(playbackSpeed.label);
    });
    runner.playbackComplete$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const currentRoom = runner.currentReplayRoom;
      if (currentRoom) {
        this.showResult(currentRoom);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onPlayClicked(): void {
    this.gameController().replayRunner.handleControls(ReplayControlType.TogglePlay);
    this.analyticsService.log(EventName.ReplayTogglePlayClicked);
  }

  public onSkipBackClicked(): void {
    this.gameController().replayRunner.handleControls(ReplayControlType.SkipBack);
    this.analyticsService.log(EventName.ReplaySkipBackClicked);
  }

  public onSkipForwardClicked(): void {
    this.gameController().replayRunner.handleControls(ReplayControlType.SkipForward);
    this.analyticsService.log(EventName.ReplaySkipForwardClicked);
  }

  public onPlaybackSpeedClicked(): void {
    this.gameController().replayRunner.handleControls(ReplayControlType.TogglePlaybackSpeed);
    this.analyticsService.log(EventName.ReplayTogglePlaybackSpeedClicked);
  }

  private async showResult(room: Room): Promise<void> {
    const roomTimer = new RoomTimer(this.translocoService);
    roomTimer.updateRoom(room);

    const resultDrawerData: DuelResultDrawerData = {
      room: {
        roomId: room.id,
        roundId: room.roundId(),
        gameId: room.gameId(),
        roomStatus: room.status(),
        statusColor: Room.statusColor(room.status()),
        roomScore: room.score(),
        roomRoundState: room.model.roundState,
        roomResult: room.result(),
        roomStatusResult: room.resultStatus(),
      },
      player: {
        roomPlayerId: null,
        roomPlayerName: null,
      },
      opponent: {
        roomOpponentId: room.opponentId(),
        roomOpponent: room.opponent(),
        roomOpponentScore: room.opponentScore(),
        roomOpponentRoundState: room.model.opponent?.roundState || 'NOT_STARTED',
      },
      payment: {
        matchCurrency: undefined,
        isFree: false,
        primaryRoomFee: room.formattedEntryFee('primary', this.themeService),
        primaryRoomPrize: room.formattedReward('primary', this.themeService),
        secondaryRoomFee: room.formattedEntryFee('secondary', this.themeService),
        secondaryRoomPrize: room.formattedReward('secondary', this.themeService),
      },
    };

    const resultDrawViewModel = new DuelResultDrawerViewModel(
      resultDrawerData,
      this.translocoService,
      this.themeService,
      roomTimer,
    );

    const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
    const modal = await this.modalController.create({
      ...modalContent(
        room.gameId() === GameId.Marbles ? MarblesResultDrawerComponent : DuelResultDrawerComponent,
        {
          viewModel: resultDrawViewModel,
          isAfterGame: false,
          isFreeGame: false,
          roomReplay: undefined,
        },
      ),
      ...getModalConfig(asSheetModal),
    });

    await modal.present();

    modal.onDidDismiss().then(() => this.onResultDismissed());
  }

  onResultDismissed(): void {
    this.resultDismissedCallback()();
  }
}
